import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Clock,
  Trash2,
  Star,
  StarOff,
  FileCode,
  ExternalLink,
  RefreshCw,
  Search
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
  getHistory,
  removeFromHistory,
  clearHistory,
  addToFavorites,
  removeFromFavorites,
  getFavorites
} from '../utils/history';
import { encodeData } from '../utils/storage';
import { languages } from '../utils/languages';

const History = ({ theme, onLoadPaste }) => {
  const [history, setHistory] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [activeTab, setActiveTab] = useState('history');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    const [historyData, favoritesData] = await Promise.all([
      getHistory(),
      getFavorites()
    ]);
    setHistory(historyData);
    setFavorites(favoritesData);
    setLoading(false);
  };

  const handleRemoveFromHistory = async (id) => {
    const updated = await removeFromHistory(id);
    setHistory(updated);
    toast.success('Removed from history');
  };

  const handleClearHistory = async () => {
    if (window.confirm('Clear all history? This cannot be undone.')) {
      await clearHistory();
      setHistory([]);
      toast.success('History cleared');
    }
  };

  const handleToggleFavorite = async (paste, isFav) => {
    if (isFav) {
      const updated = await removeFromFavorites(paste.id);
      setFavorites(updated);
      toast.success('Removed from favorites');
    } else {
      const updated = await addToFavorites(paste);
      setFavorites(updated);
      toast.success('Added to favorites');
    }
  };

  const getShareUrl = (paste) => {
    const encoded = encodeData(paste);
    return `${window.location.origin}/#/view/${encoded}`;
  };

  const getLanguageLabel = (value) => {
    const lang = languages.find(l => l.value === value);
    return lang ? lang.label : 'Plain Text';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
    return date.toLocaleDateString();
  };

  const filteredItems = (activeTab === 'history' ? history : favorites).filter(
    item =>
      item.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.content?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const isFavorite = (id) => favorites.some(f => f.id === id);

  if (loading) {
    return (
      <div className="history-loading">
        <RefreshCw className="spin" size={24} />
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="history-container">
      <div className="history-header">
        <div className="history-tabs">
          <button
            type="button"
            className={`tab-btn ${activeTab === 'history' ? 'active' : ''}`}
            onClick={() => setActiveTab('history')}
          >
            <Clock size={16} />
            History ({history.length})
          </button>
          <button
            type="button"
            className={`tab-btn ${activeTab === 'favorites' ? 'active' : ''}`}
            onClick={() => setActiveTab('favorites')}
          >
            <Star size={16} />
            Favorites ({favorites.length})
          </button>
        </div>

        <div className="history-actions">
          <div className="search-box">
            <Search size={16} />
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          {activeTab === 'history' && history.length > 0 && (
            <button type="button" className="clear-btn" onClick={handleClearHistory}>
              <Trash2 size={16} />
              Clear All
            </button>
          )}
        </div>
      </div>

      <div className="history-list">
        {filteredItems.length === 0 ? (
          <div className="history-empty">
            <p>
              {searchQuery
                ? 'No matching items found'
                : activeTab === 'history'
                ? 'No history yet. Create your first paste!'
                : 'No favorites yet. Star a paste to save it here.'}
            </p>
            <Link to="/" className="action-btn primary">
              Create New Paste
            </Link>
          </div>
        ) : (
          filteredItems.map((item) => (
            <div key={item.id} className="history-item">
              <div className="item-content">
                <div className="item-header">
                  <h3 className="item-title">{item.title || 'Untitled'}</h3>
                  <span className="item-language">
                    <FileCode size={12} />
                    {getLanguageLabel(item.language)}
                  </span>
                </div>
                <p className="item-preview">
                  {item.content?.substring(0, 100)}
                  {item.content?.length > 100 ? '...' : ''}
                </p>
                <div className="item-meta">
                  <span className="item-date">
                    <Clock size={12} />
                    {formatDate(item.savedAt || item.createdAt)}
                  </span>
                  <span className="item-stats">
                    {item.content?.split('\n').length || 0} lines
                  </span>
                </div>
              </div>

              <div className="item-actions">
                <button
                  type="button"
                  className={`icon-btn ${isFavorite(item.id) ? 'favorited' : ''}`}
                  onClick={() => handleToggleFavorite(item, isFavorite(item.id))}
                  title={isFavorite(item.id) ? 'Remove from favorites' : 'Add to favorites'}
                >
                  {isFavorite(item.id) ? <Star size={16} /> : <StarOff size={16} />}
                </button>
                <button
                  type="button"
                  className="icon-btn"
                  onClick={() => onLoadPaste(item)}
                  title="Load in editor"
                >
                  <ExternalLink size={16} />
                </button>
                {activeTab === 'history' && (
                  <button
                    type="button"
                    className="icon-btn danger"
                    onClick={() => handleRemoveFromHistory(item.id)}
                    title="Remove from history"
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default History;
